"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDatasourcesStore } from "@/components/provider/DatasourcesStoreProvider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar-lazy";
import { Label } from "@/components/ui/label";
import FieldError from "@/components/FieldError";
import FormGuard from "@/components/FormGuard";
import React from "react";
import { ReservationStoryblok } from "@/component-types-sb";
import {
  BOOKING_FAILED_MESSAGE,
  BOOKING_INVALID_MESSAGE,
  BookingField,
  BookingFieldErrors,
  hasBookingErrors,
  invalidFieldProps,
  validateBooking,
} from "@/lib/booking";

type ButtonProps = React.ComponentProps<typeof Button>;

// Global state to track which modal is open
let globalModalId: string | null = null;
const modalListeners = new Set<() => void>();

const ButtonReserve = ({ children, variant, ...props }: ButtonProps) => {
  const datasources = useDatasourcesStore((state) => state.datasources);
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = React.useState<BookingFieldErrors>({});
  const [isOpen, setIsOpen] = React.useState(false);

  // Generate unique ID for this modal instance
  const [modalId] = React.useState(() =>
    Math.random().toString(36).substr(2, 9)
  );

  // Function to format date in German
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("de-DE", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to update state when global modal changes
  const updateModalState = React.useCallback(() => {
    setIsOpen(globalModalId === modalId);
  }, [modalId]);

  React.useEffect(() => {
    // Add this modal to the listeners set
    modalListeners.add(updateModalState);

    const checkHash = () => {
      if (window.location.hash === "#reserve") {
        // Only open this modal if no other modal is currently open
        if (globalModalId === null) {
          globalModalId = modalId;
          // Notify all modals to update their state
          modalListeners.forEach((listener) => listener());
        }
      }
    };

    // Check on mount
    checkHash();

    // Listen for hash changes
    window.addEventListener("hashchange", checkHash);

    return () => {
      // Clean up
      modalListeners.delete(updateModalState);
      window.removeEventListener("hashchange", checkHash);

      // If this modal was the open one, clear the global state
      if (globalModalId === modalId) {
        globalModalId = null;
      }
    };
  }, [modalId, updateModalState]);

  // Handle dialog state changes
  const handleOpenChange = (open: boolean) => {
    if (open) {
      // Only allow opening if no other modal is open
      if (globalModalId === null) {
        globalModalId = modalId;
        setIsOpen(true);
        // Notify other modals
        modalListeners.forEach((listener) => listener());
      }
    } else {
      // Close this modal
      if (globalModalId === modalId) {
        globalModalId = null;
        setIsOpen(false);

        // Remove hash if present
        if (window.location.hash === "#reserve") {
          window.history.replaceState(
            null,
            "",
            window.location.pathname + window.location.search
          );
        }

        // Notify other modals
        modalListeners.forEach((listener) => listener());
      }
    }
  };

  const clearFieldError = (e: React.FormEvent<HTMLFormElement>) => {
    const field = (e.target as HTMLInputElement).name as BookingField;
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSuccess(null);
    setError(null);
    setFieldErrors({});

    const formData = new FormData(e.currentTarget);
    const reservationData: ReservationStoryblok = {
      component: "reservation",
      _uid: "",
    };
    formData.forEach((value, key) => {
      reservationData[key] = value;
    });
    reservationData.date = date?.toISOString();

    const validationErrors = validateBooking(reservationData);
    if (hasBookingErrors(validationErrors)) {
      setFieldErrors(validationErrors);
      setError(BOOKING_INVALID_MESSAGE);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch("/api/storyblok/addReservation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(reservationData),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFieldErrors(result.fieldErrors || {});
        throw new Error(result.message || BOOKING_FAILED_MESSAGE);
      }

      setSuccess(
        result.message || "Ihre Reservierung wurde erfolgreich übermittelt!"
      );
      (e.target as HTMLFormElement).reset();
      setDate(new Date());
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message || BOOKING_FAILED_MESSAGE
          : "Unbekannter Fehler"
      );
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant={variant} {...props}>
          {children}
        </Button>
      </DialogTrigger>
      <DialogContent className="overflow-y-auto max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{datasources?.reserve.title}</DialogTitle>
          <DialogDescription>
            {datasources?.reserve.description}
          </DialogDescription>
        </DialogHeader>
        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-green-800">{success}</p>
          </div>
        ) : (
          <form className="grid gap-4" onSubmit={handleSubmit} onChange={clearFieldError} noValidate>
            <div className="grid gap-2">
              <Label htmlFor="reserve-name">Name *</Label>
              <Input
                id="reserve-name"
                name="name"
                type="text"
                placeholder="Name"
                required
                {...invalidFieldProps(fieldErrors, "name", "reserve-name")}
              />
              <FieldError id="reserve-name-error" message={fieldErrors.name} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reserve-phone">Telefon *</Label>
              <Input
                id="reserve-phone"
                name="phone"
                type="tel"
                placeholder="Telefon"
                required
                {...invalidFieldProps(fieldErrors, "phone", "reserve-phone")}
              />
              <FieldError id="reserve-phone-error" message={fieldErrors.phone} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reserve-email">Email *</Label>
              <Input
                id="reserve-email"
                name="email"
                type="email"
                placeholder="Email"
                required
                {...invalidFieldProps(fieldErrors, "email", "reserve-email")}
              />
              <FieldError id="reserve-email-error" message={fieldErrors.email} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reserve-message">Nachricht</Label>
              <Textarea
                id="reserve-message"
                name="message"
                placeholder="Nachricht"
                rows={3}
                className="resize-none"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reserve-people">Anzahl Personen *</Label>
              <Input
                id="reserve-people"
                name="people"
                type="number"
                placeholder="Anzahl Personen"
                required
                min={1}
                {...invalidFieldProps(fieldErrors, "people", "reserve-people")}
              />
              <FieldError id="reserve-people-error" message={fieldErrors.people} />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="reserve-time">Uhrzeit *</Label>
              <Input
                id="reserve-time"
                name="time"
                type="time"
                placeholder="Uhrzeit"
                defaultValue="18:00"
                required
                className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                {...invalidFieldProps(fieldErrors, "time", "reserve-time")}
              />
              <FieldError id="reserve-time-error" message={fieldErrors.time} />
            </div>

            <div className="grid gap-2">
              <Label>Datum *</Label>

              <Input
                className="w-full justify-start text-left font-normal"
                {...invalidFieldProps(fieldErrors, "date", "reserve-date")}
                value={date ? formatDate(date) : "Datum wählen"}
                readOnly
              />

              <Calendar
                mode="single"
                selected={date}
                onSelect={(selectedDate) => {
                  setDate(selectedDate);
                  setFieldErrors((prev) => ({ ...prev, date: undefined }));
                }}
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0))
                }
                formatters={{
                  formatWeekdayName: (date) =>
                    date.toLocaleDateString("de-DE", { weekday: "short" }),
                  formatMonthCaption: (date) =>
                    date.toLocaleDateString("de-DE", {
                      month: "long",
                      year: "numeric",
                    }),
                }}
              />
              <FieldError id="reserve-date-error" message={fieldErrors.date} />
            </div>

            <FormGuard />

            <Button type="submit" variant="default" disabled={isSubmitting}>
              {isSubmitting ? "Wird gesendet..." : "Reservieren"}
            </Button>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ButtonReserve;
